/**
 * ReferenceListContext — kontekst przekazujący dane kartoteki do kreatora listy
 * encji-odwołania osadzonego w ModuleTemplateEditor.
 *
 * Problem: `SystemModuleListBuilderPage` jest generycznym komponentem — nie wie, czy
 * wyświetla encję własną szablonu (pola w template entity), czy encję-odwołanie do
 * kartoteki systemowej (pola kartoteki, bez template entity.fields).
 *
 * Rozwiązanie (bez zmiany sygnatur 6 paneli):
 *  - `ReferenceListProvider` opakowuje `<SystemModuleListBuilderPage>` gdy
 *    renderowana jest encja-odwołanie.
 *  - `useColumnLabel` (list-column-registry.ts) sprawdza ten kontekst gdy
 *    `templateEntity.fields` jest puste — pobiera etykiety z kartoteki.
 *  - `useTableLayout` sprawdza ten kontekst po udanym zapisie w trybie template
 *    i wywołuje `onViewConfigPersisted` (clobber guard).
 */

import { createContext, useContext } from 'react';
import type { ListConfig } from '@saas/shared';

import type {
  KartotekaBuilderField,
  KartotekaDefaultListConfig,
} from '@/lib/kartoteki/kartoteka-definition';

/**
 * Pozycja katalogu pól podawanego kreatorowi listy (`availableFields`): `id` = slug pola,
 * `name` = etykieta. Katalog jest źródłem NAZW pól we wszystkich zakładkach kreatora z listą
 * kolumn (Kolumny / Widgety / Szybkie filtry / Kanban / Kalendarz).
 *
 * `fieldType` (typ pola z katalogu, np. `date`/`timestamp`) i `isFilterable` wypełniają tylko
 * konteksty, które je znają — kartoteki systemowe (`scope='system'`). Zakładka „Kalendarz"
 * w trybie `dateFieldsOnly` bierze z katalogu same pola (kalendarz nie zależy od kolumn układu):
 * listy „Pole statusu" i „Pole etykiety" oferują wszystkie jego pozycje (oraz kolumny strony spoza
 * katalogu z rejestru kolumn — `resolveListColumnSet`), a „Pole daty startu/końca" wyłącznie
 * pola katalogu, zawężone po typie: BRAK `fieldType` znaczy tam „nie data" (pole bez typu nie
 * jest oferowane jako data), a `isFilterable === false` wyklucza pole jako pole STARTU (brak
 * flagi = nieznane, nie wyklucza; pole końca nie jest filtrem nagłówka, tylko częścią warunku
 * nakładania zakresu na miesiąc — serwer bierze je w parze z polem startu dowolnego rodzaju bez
 * wymogu flagi, więc jego filtrowalność nie ma znaczenia). Poza `dateFieldsOnly` (moduły własne,
 * szablony) typy nie są używane.
 */
export interface ReferenceListAvailableField {
  id: string;
  name: string;
  /** Typ pola katalogu (`date`, `timestamp`, `varchar`, …); niezdefiniowany = typ nieznany. */
  fieldType?: string;
  /**
   * Czy serwer potrafi filtrować listę po tym polu (kalendarz zawęża listę filtrem zakresu
   * wyłącznie na polu STARTU; dla pola końca flaga nie ma znaczenia). Pola dodatkowe (ext):
   * flaga `is_filterable` z katalogu (`isExtFieldFilterEligible` w
   * `apps/api/lib/contractors/apply-header-filters.ts`). Pola rdzenne: zawsze `true` — silnik
   * list wyprowadza filtr ze WSZYSTKICH kolumn rdzennych z whitelisty bez sprawdzania flagi
   * (`deriveAutoHeaderFilterColumns`, `apps/api/lib/list-engine/auto-header-filter-columns.ts`).
   * Niezdefiniowane = nieznane.
   */
  isFilterable?: boolean;
}

export interface ReferenceListContextValue {
  /** Pola kartoteki systemowej — źródło etykiet kolumn zamiast template entity.fields. */
  kartotekaFields: KartotekaBuilderField[];
  /**
   * Etap 2 B — domyślny list config kartoteki (z buildDefaultListConfig, slug-driven).
   * Używany przez useTableLayout jako seed gdy brak zapisanego per-moduł listConfig
   * i encja jest odwołaniem do kartoteki systemowej.
   */
  kartotekaDefaultListConfig?: KartotekaDefaultListConfig;
  /**
   * Callback wywoływany po udanym PATCH widoku listy (clobber guard).
   * Synchronizuje lokalny stan edytora BEZ setDirty=true — config już na serwerze.
   */
  onViewConfigPersisted: (listConfig: ListConfig) => void;
  /**
   * Opcjonalny interceptor zapisu — gdy podany, useTableLayout wywołuje go ZAMIAST
   * patchTemplateViewConfig (API PATCH). Używany przez builder widoku listy pozycji
   * menu (MenuItemViewBuilderModal): zapis trafia do node.view.listConfig w lokalnym
   * stanie drzewa menu, nie do backendu encji szablonu.
   *
   * `onSuccess` wewnątrz saveMutation wciąż jest wywoływany po wywołaniu tego
   * handlera (traktujemy go jak resolved Promise void).
   */
  onListConfigSave?: (listConfig: ListConfig) => void;
  /**
   * Seed początkowy dla trybu „edycja nazwanego widoku listy kartoteki" (scope='system'
   * osadzony, poza szablonem). Gdy podany RAZEM z `onListConfigSave` i BEZ template-tableId,
   * `useTableLayout` bierze ten config jako aktywny layout (zamiast pobierać layouty modułu
   * systemowego), a zapis przechwytuje `onListConfigSave`. Kombinacja (onListConfigSave &&
   * brak templateRef) nie występuje w żadnym istniejącym użyciu — dlatego jest bezpieczna.
   */
  initialListConfig?: ListConfig | null;
  /**
   * Pełna lista dostępnych pól kartoteki do palety „Dodaj kolumnę" w ListColumnsPanel.
   * Ustawiana przez MenuItemViewBuilderModal dla obu typów encji:
   *  - reference_to_system_entity: pola z useKartotekaDefinition (kartotekaFields)
   *  - new_table: pola z entity.fields (pola własne szablonu)
   * Gdy undefined — sekcja „Dodaj kolumnę" nie pojawia się (zero regresji dla innych
   * kontekstów, np. standalone builder kartoteki systemowej).
   *
   * Scope `system` (`SystemModuleListBuilderPage`) podaje tu katalog pól modułu (z `fieldType`
   * i `isFilterable`) wszystkim zakładkom z listą kolumn — z niego biorą nazwy pól
   * (`useColumnLabel`), a Kalendarz także typ i filtrowalność (patrz `ReferenceListAvailableField`).
   */
  availableFields?: ReferenceListAvailableField[];
  /**
   * `true` = katalog `availableFields` NIE jest zawężony do pól, które lista klienta umie pokazać
   * (z włączoną opcją „Pokaż na liście"): zawiera wszystkie pola definicji kartoteki. Tak jest w
   * panelu ITE (`KartotekaViewListBuilder`) — definicja kartoteki (`GET /admin/kartoteki/{slug}/
   * definition`) nie niesie tej flagi przy polach, a domyślny układ listy z serwera obejmuje tylko
   * pola rdzenne (pola dodane/pula klasy z „Pokaż na liście" — np. `date_from` w „specjalne" —
   * w nim nie ma), więc nie da się wiarygodnie wywnioskować, które pola klient narysuje.
   * Zakładka „Kalendarz" (`ListCalendarPanel`, tryb `dateFieldsOnly`) dopisuje wtedy ogólną
   * podpowiedź; opcje list pól zostają takie same. Niezdefiniowane/`false` = katalog jest zawężony
   * (zapytanie `/system-modules/{slug}/fields` kreatora u klienta) albo nie dotyczy.
   */
  catalogListFlagUnknown?: boolean;
  /**
   * Callback informujący parenta (MenuItemViewBuilderModal) o zmianie stanu dirty.
   * Wywoływany przez ListColumnsPanel przy każdej modyfikacji lokalnych kolumn
   * (toggle widoczności, reorder, zmiana szerokości/etykiety, dodanie/usunięcie kolumny)
   * oraz po pomyślnym zapisie (dirty=false).
   * Gdy undefined — brak trackingu dirty (zero regresji dla innych kontekstów).
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Czy built-iny `record_name`/`record_number` są PRAWDZIWYMI kolumnami w tym
   * kontekście — czyli encja ma fizyczną kolumnę nazwy/numeru wyświetlanego rekordu
   * (moduł własny / szablon, `ddlStrategy='new_table'`). Dla encji-odwołania do
   * kartoteki systemowej (`reference_to_system_entity`) i dla kartotek systemowych
   * (towary, kontrahenci, dokumenty, pracownicy) built-iny NIE istnieją — `false`/undefined.
   * Używane WYŁĄCZNIE przez `ListColumnsPanel` do bramkowania palety „Dodaj kolumnę"
   * i soft-healu zapisu (zbiór dozwolonych id). Domyślnie `false` (bezpieczny fallback —
   * kontekst, który nie ustawia flagi jawnie, jest kartoteką systemową).
   */
  builtinColumnsAvailable?: boolean;
  /**
   * Slug rejestru etykiet (`list-column-registry.ts`, `getRegisteredColumnIds`) dla kolumn
   * spoza katalogu (np. `company`, kolumny procesu). Domyślnie `ListColumnsPanel` wylicza go
   * z `slug` (`slug.split('::')[0]`) — działa dla slugów systemowych, ale w menu-embed `slug`
   * ma postać `template:<code>:<entitySlug>:<viewSlug>` (bez `::`), więc split zwraca cały
   * string i rejestr nigdy nie trafia. Ustawiane przez `MenuItemViewBuilderModal` na
   * `entity.referenceSystemModuleSlug` dla encji `reference_to_system_entity` — realny slug
   * kartoteki systemowej, pod którym żyją nadpisania w rejestrze. Gdy undefined —
   * `ListColumnsPanel` wraca do liczenia z `slug` (zachowanie bez zmian).
   */
  columnRegistrySlug?: string;
}

export const ReferenceListContext = createContext<ReferenceListContextValue | null>(null);

/** Odczytuje kontekst encji-odwołania. null = brak kontekstu (normalne użycie). */
export function useReferenceListContext(): ReferenceListContextValue | null {
  return useContext(ReferenceListContext);
}
